import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronRight, Trash2, UserPlus, Users, X } from "react-feather";
import { toast } from "react-toastify";
import {
  assignTeachersToTeacherLeader,
  createTeacherLeader,
  getTeacherLeaders,
  getTeacherLeaderTeachers,
  getTeachers,
  markTeacherLeader,
  removeTeacherFromTeacherLeader,
  teacherProfile,
} from "../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import { isTeacherLeaderDisabled } from "../../../../utils/permissions";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Select,
  TextInput,
  Toolbar,
} from "../../../../ui";
import "./teacherLeader.scss";

type PersonRow = {
  teacherId: string;
  userId?: string;
  teacherName: string;
  emailId?: string;
  mobileNumber?: string;
};

const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+65", "+61"];

// Number of avatar tones in teacherLeader.scss (.tl-avatar--tone-N).
const AVATAR_TONES = 6;

const COUNTRY_CODE_OPTIONS = COUNTRY_CODES.map((code) => ({ value: code, label: code }));

const EMPTY_LEADER_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+91",
  mobileNumber: "",
};

const isArtiumEmail = (value: string): boolean =>
  /^[^\s@]+@artiumacademy\.com$/i.test(value.trim());

const teacherDisplayName = (row: any): string =>
  String(row?.fullName || "")
    .replace(/\u2060/g, "")
    .trim() ||
  [row?.firstName, row?.lastName].filter(Boolean).join(" ").trim() ||
  row?.teacherName ||
  row?.name ||
  "";

const asId = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  if (!text || text === "undefined" || text === "null") return null;
  const numeric = Number(text);
  return Number.isFinite(numeric) ? String(numeric) : text;
};

const emailOf = (row: any): string =>
  row?.emailId || row?.email ? String(row.emailId || row.email) : "";

const mobileOf = (row: any): string => (row?.mobileNumber ? String(row.mobileNumber) : "");

const initialsOf = (name: string): string =>
  name
    .replace(/\u2060/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const avatarTone = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % AVATAR_TONES;
};

const matchesQuery = (person: PersonRow, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [person.teacherName, person.emailId, person.mobileNumber, person.userId, person.teacherId]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
};

const mapLeader = (row: any): PersonRow | null => {
  const userId = asId(row?.userId ?? row?.teacherUserId);
  const teacherId = asId(row?.teacherId ?? row?.id) || userId;
  if (!userId && !teacherId) return null;
  return {
    teacherId: (teacherId || userId) as string,
    userId: userId || undefined,
    teacherName: teacherDisplayName(row) || `Teacher ${teacherId || userId}`,
    emailId: emailOf(row),
    mobileNumber: mobileOf(row),
  };
};

const mapSearchTeacher = (row: any): PersonRow | null => {
  const teacherId = asId(row?.teacherId ?? row?.id);
  if (!teacherId) return null;
  return {
    teacherId,
    userId: asId(row?.userId ?? row?.teacherUserId) || undefined,
    teacherName: teacherDisplayName(row) || `Teacher ${teacherId}`,
    emailId: emailOf(row),
    mobileNumber: mobileOf(row),
  };
};

const mapAssignedTeacher = (row: any): PersonRow | null => {
  const teacherId = asId(row?.teacherId ?? row?.id ?? row?.userId ?? row?.teacherUserId);
  if (!teacherId) return null;
  return {
    teacherId,
    userId: asId(row?.userId ?? row?.teacherUserId) || undefined,
    teacherName: teacherDisplayName(row) || `Teacher ${teacherId}`,
    emailId: emailOf(row),
    mobileNumber: mobileOf(row),
  };
};

const personKeys = (item: PersonRow): string[] =>
  [item.teacherId, item.userId, item.emailId?.toLowerCase()].filter(Boolean) as string[];

const resolveTeacherUserId = async (person: PersonRow): Promise<string | null> => {
  if (person.userId) return person.userId;
  if (!person.teacherId) return null;
  try {
    const profile = await teacherProfile(person.teacherId);
    return asId(profile?.teacherProfileDetails?.userId ?? profile?.userId);
  } catch (error) {
    console.error("Failed to resolve teacherUserId:", error);
    return null;
  }
};

const Avatar: React.FC<{ name: string; seed?: string; size?: "sm" | "md" }> = ({ name, seed, size = "md" }) => (
  <span
    className={`tl-avatar tl-avatar--tone-${avatarTone(seed || name)}${size === "sm" ? " tl-avatar--sm" : ""}`}
    aria-hidden
  >
    {initialsOf(name)}
  </span>
);

const TeacherLeader: React.FC = () => {
  const readOnly = isTeacherLeaderDisabled();
  const [leaders, setLeaders] = useState<PersonRow[]>([]);
  const [loadingLeaders, setLoadingLeaders] = useState(false);
  const [leaderFilter, setLeaderFilter] = useState("");
  const [teacherToUnmark, setTeacherToUnmark] = useState<PersonRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addLeaderOpen, setAddLeaderOpen] = useState(false);
  const [leaderForm, setLeaderForm] = useState(EMPTY_LEADER_FORM);

  const [selectedLeader, setSelectedLeader] = useState<PersonRow | null>(null);
  const [assigned, setAssigned] = useState<PersonRow[]>([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [assignQuery, setAssignQuery] = useState("");
  const [assignSuggestions, setAssignSuggestions] = useState<PersonRow[]>([]);
  const [assignSearching, setAssignSearching] = useState(false);
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<PersonRow[]>([]);
  const [teacherToRemove, setTeacherToRemove] = useState<PersonRow | null>(null);
  const [assignedFilter, setAssignedFilter] = useState("");

  const assignTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assignWrapRef = useRef<HTMLDivElement | null>(null);
  const assignedRef = useRef<PersonRow[]>([]);
  const pendingAssignRef = useRef<PersonRow[]>([]);
  assignedRef.current = assigned;
  pendingAssignRef.current = pendingAssign;

  const visibleLeaders = useMemo(
    () => leaders.filter((leader) => matchesQuery(leader, leaderFilter)),
    [leaders, leaderFilter]
  );

  const visibleAssigned = useMemo(
    () => assigned.filter((teacher) => matchesQuery(teacher, assignedFilter)),
    [assigned, assignedFilter]
  );

  const loadLeaders = useCallback(async () => {
    setLoadingLeaders(true);
    try {
      const list = await getTeacherLeaders();
      setLeaders((list || []).map(mapLeader).filter((row): row is PersonRow => Boolean(row)));
    } catch (error: any) {
      console.error("Error loading teacher leaders:", error);
      toast.error(error?.response?.data?.message || "Failed to load teacher leaders.");
      setLeaders([]);
    } finally {
      setLoadingLeaders(false);
    }
  }, []);

  const loadAssigned = useCallback(async (leader: PersonRow) => {
    const userId = leader.userId;
    if (!userId) {
      toast.error("This leader has no user id, so assigned teachers cannot be loaded.");
      setAssigned([]);
      return;
    }
    setLoadingAssigned(true);
    try {
      const list: any[] = (await getTeacherLeaderTeachers(userId)) || [];
      setAssigned(
        list.map(mapAssignedTeacher).filter((row: PersonRow | null): row is PersonRow => row != null)
      );
    } catch (error: any) {
      console.error("Error loading assigned teachers:", error);
      toast.error(error?.response?.data?.message || "Failed to load assigned teachers.");
      setAssigned([]);
    } finally {
      setLoadingAssigned(false);
    }
  }, []);

  useEffect(() => {
    void loadLeaders();
  }, [loadLeaders]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!assignWrapRef.current?.contains(target)) {
        setAssignSuggestions([]);
        setAssignMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const searchTeachersToAssign = async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setAssignSuggestions([]);
      return;
    }
    setAssignSearching(true);
    try {
      const digitsOnly = q.replace(/\D/g, "");
      const payload = digitsOnly.length >= 4 ? { mobileNumber: digitsOnly } : { name: q };
      const list = await getTeachers(0, 20, payload);
      const existing = new Set(
        [...assignedRef.current, ...pendingAssignRef.current].flatMap(personKeys)
      );
      const mapped = ((list || []) as any[])
        .map(mapSearchTeacher)
        .filter((row): row is PersonRow => {
          if (!row) return false;
          return !personKeys(row).some((key) => existing.has(key));
        });
      setAssignSuggestions(mapped);
    } catch (error) {
      console.error("Assign search failed:", error);
      setAssignSuggestions([]);
    } finally {
      setAssignSearching(false);
    }
  };

  const handleAssignQueryChange = (value: string) => {
    setAssignQuery(value);
    setAssignMenuOpen(true);
    if (assignTimer.current) clearTimeout(assignTimer.current);
    assignTimer.current = setTimeout(() => {
      void searchTeachersToAssign(value);
    }, 350);
  };

  const openLeader = (leader: PersonRow) => {
    setSelectedLeader(leader);
    setAssignQuery("");
    setAssignSuggestions([]);
    setAssignMenuOpen(false);
    setPendingAssign([]);
    setTeacherToRemove(null);
    setAssignedFilter("");
    void loadAssigned(leader);
  };

  const closeLeader = () => {
    setSelectedLeader(null);
    setAssigned([]);
    setAssignQuery("");
    setAssignSuggestions([]);
    setAssignMenuOpen(false);
    setPendingAssign([]);
    setTeacherToRemove(null);
    setAssignedFilter("");
  };

  const addPendingTeacher = (teacher: PersonRow) => {
    setPendingAssign((prev) =>
      prev.some((item) => item.teacherId === teacher.teacherId) ? prev : [...prev, teacher]
    );
    setAssignSuggestions((prev) => prev.filter((item) => item.teacherId !== teacher.teacherId));
  };

  const closeUnmarkModal = () => {
    if (submitting) return;
    setTeacherToUnmark(null);
  };

  const closeRemoveModal = () => {
    if (submitting) return;
    setTeacherToRemove(null);
  };

  const openAddLeader = () => {
    setLeaderFilter("");
    setLeaderForm(EMPTY_LEADER_FORM);
    setAddLeaderOpen(true);
  };

  const closeAddLeader = () => {
    if (submitting) return;
    setAddLeaderOpen(false);
    setLeaderForm(EMPTY_LEADER_FORM);
  };

  const confirmCreateLeader = async () => {
    const firstName = leaderForm.firstName.trim();
    const lastName = leaderForm.lastName.trim();
    const email = leaderForm.email.trim().toLowerCase();
    const countryCode = leaderForm.countryCode.trim() || "+91";
    const mobileNumber = leaderForm.mobileNumber.replace(/\D/g, "");
    if (!firstName || !lastName) {
      toast.warning("Please enter first name and last name.");
      return;
    }
    if (!isArtiumEmail(email)) {
      toast.warning("Email must be an @artiumacademy.com address.");
      return;
    }
    if (!mobileNumber || (countryCode === "+91" && mobileNumber.length !== 10) || mobileNumber.length < 6) {
      toast.warning(countryCode === "+91" ? "Please enter a 10-digit mobile number." : "Please enter a valid mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await createTeacherLeader({ firstName, lastName, email, countryCode, mobileNumber });
      toast.success(`${firstName} ${lastName} is now a Teacher Leader.`);
      trackEvent(MixpanelEvents.TEACHER_LEADER_CREATED, { email, mobileNumber });
      setAddLeaderOpen(false);
      setLeaderForm(EMPTY_LEADER_FORM);
      setLeaderFilter("");
      await loadLeaders();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to add Teacher Leader.";
      toast.error(message);
      trackEvent(MixpanelEvents.TEACHER_LEADER_CREATE_FAILED, { email, errorMessage: message });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmUnmark = async () => {
    if (!teacherToUnmark) return;
    setSubmitting(true);
    try {
      const teacherUserId = await resolveTeacherUserId(teacherToUnmark);
      if (!teacherUserId) {
        toast.error("Could not resolve teacher user id for this leader.");
        return;
      }
      await markTeacherLeader(teacherUserId, false);
      toast.success(`${teacherToUnmark.teacherName} is no longer a Teacher Leader.`);
      trackEvent(MixpanelEvents.TEACHER_LEADER_ROLE_REMOVED, {
        teacherId: teacherToUnmark.teacherId,
        teacherUserId,
      });
      setTeacherToUnmark(null);
      if (
        selectedLeader &&
        (selectedLeader.userId === teacherToUnmark.userId ||
          selectedLeader.teacherId === teacherToUnmark.teacherId)
      ) {
        closeLeader();
      }
      await loadLeaders();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to remove Teacher Leader role.";
      toast.error(message);
      trackEvent(MixpanelEvents.TEACHER_LEADER_ROLE_ASSIGN_FAILED, {
        teacherId: teacherToUnmark.teacherId,
        errorMessage: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmAssign = async () => {
    if (!selectedLeader?.userId || pendingAssign.length === 0) return;
    setSubmitting(true);
    try {
      const teacherIds = Array.from(
        new Set([...assigned, ...pendingAssign].map((item) => item.teacherId))
      );
      await assignTeachersToTeacherLeader(selectedLeader.userId, teacherIds);
      toast.success("Teachers assigned.");
      trackEvent(MixpanelEvents.TEACHER_LEADER_TEACHERS_ASSIGNED, {
        leaderUserId: selectedLeader.userId,
        teacherCount: teacherIds.length,
      });
      setPendingAssign([]);
      setAssignQuery("");
      setAssignSuggestions([]);
      await loadAssigned(selectedLeader);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to assign teachers.";
      toast.error(message);
      trackEvent(MixpanelEvents.TEACHER_LEADER_TEACHERS_ASSIGN_FAILED, {
        leaderUserId: selectedLeader.userId,
        errorMessage: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemoveAssigned = async () => {
    if (!selectedLeader?.userId || !teacherToRemove) return;
    setSubmitting(true);
    try {
      await removeTeacherFromTeacherLeader(selectedLeader.userId, teacherToRemove.teacherId);
      toast.success(`${teacherToRemove.teacherName} was removed from this leader.`);
      trackEvent(MixpanelEvents.TEACHER_LEADER_TEACHERS_REMOVED, {
        leaderUserId: selectedLeader.userId,
        teacherId: teacherToRemove.teacherId,
      });
      setTeacherToRemove(null);
      await loadAssigned(selectedLeader);
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to remove teacher from this leader.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderSearchResults = (
    items: PersonRow[],
    loading: boolean,
    query: string,
    onPick: (teacher: PersonRow) => void,
    actionLabel: string,
    matchingLeaders = 0
  ) => {
    if (loading) {
      return (
        <div className="tl-menu">
          <div className="tl-menu__status">
            <Loader label="Searching…" />
          </div>
        </div>
      );
    }
    if (items.length > 0) {
      return (
        <ul className="tl-menu" aria-label="Matching teachers">
          {items.map((teacher) => (
            <li key={teacher.teacherId}>
              <button
                type="button"
                className="tl-result"
                onClick={() => onPick(teacher)}
                disabled={readOnly || submitting}
              >
                <Avatar name={teacher.teacherName} seed={teacher.teacherId} size="sm" />
                <span className="tl-result__meta">
                  <span className="tl-result__name">{teacher.teacherName}</span>
                  <span className="tl-result__sub">
                    {[teacher.emailId, teacher.mobileNumber ? `ID ${teacher.teacherId}` : `Teacher ID ${teacher.teacherId}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="tl-result__action">{actionLabel}</span>
              </button>
            </li>
          ))}
        </ul>
      );
    }
    if (query.trim().length >= 2 && matchingLeaders === 0) {
      return (
        <div className="tl-menu">
          <div className="tl-menu__status">No teachers found.</div>
        </div>
      );
    }
    if (query.trim().length >= 2 && matchingLeaders > 0) {
      return (
        <div className="tl-menu">
          <div className="tl-menu__status">Already a teacher leader — open them from the list below.</div>
        </div>
      );
    }
    return null;
  };

  const leaderRows = addLeaderOpen ? leaders : visibleLeaders;

  return (
    <Page>
      {!selectedLeader ? (
        <>
          <PageHeader
            eyebrow="Teacher Operations"
            title="Teacher Leader"
            subtitle={
              loadingLeaders
                ? "Loading leaders…"
                : `${leaders.length} ${leaders.length === 1 ? "leader" : "leaders"} · Add a leader, then open them to assign teachers.`
            }
            actions={
              !readOnly ? (
                <Button
                  variant="primary"
                  icon={<UserPlus size={16} aria-hidden />}
                  onClick={openAddLeader}
                  disabled={addLeaderOpen}
                >
                  Add Teacher Leader
                </Button>
              ) : null
            }
          />

          <Card title="Teacher leaders" className={`tl-card${addLeaderOpen ? " is-dimmed" : ""}`}>
            {!addLeaderOpen ? (
              <Toolbar>
                <SearchInput
                  className="ds-toolbar__grow"
                  aria-label="Filter leaders"
                  placeholder="Filter leaders by name, email, or mobile"
                  value={leaderFilter}
                  onChange={(e) => setLeaderFilter(e.target.value)}
                />
              </Toolbar>
            ) : null}

            {loadingLeaders ? (
              <Loader label="Loading leaders…" />
            ) : leaders.length === 0 ? (
              <EmptyState
                icon={<Users size={22} aria-hidden />}
                title="No teacher leaders yet"
                description={
                  readOnly
                    ? "Teacher leaders will appear here once they are added."
                    : "Use Add Teacher Leader to give someone the role, then open them to assign teachers."
                }
              />
            ) : leaderRows.length === 0 ? (
              <EmptyState
                icon={<Users size={22} aria-hidden />}
                title="No leaders match that filter"
                description="Try part of the name, the email or the mobile number, or clear the filter."
              />
            ) : (
              <div className="tl-people">
                {leaderRows.map((leader) => (
                  <div
                    key={leader.userId || leader.teacherId}
                    className="tl-person"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${leader.teacherName}`}
                    onClick={() => openLeader(leader)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") openLeader(leader);
                    }}
                  >
                    <Avatar name={leader.teacherName} seed={leader.userId || leader.teacherId} />
                    <div className="tl-person__main">
                      <p className="tl-person__name">{leader.teacherName}</p>
                      <p className="tl-person__email">{leader.emailId || "No email"}</p>
                    </div>
                    <div className="tl-person__side">
                      <div>{leader.mobileNumber || "—"}</div>
                      <div className="tl-person__id">User ID {leader.userId || "—"}</div>
                    </div>
                    <div className="tl-person__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="tl-danger-ghost"
                        title="Remove role"
                        aria-label={`Remove Teacher Leader role from ${leader.teacherName}`}
                        icon={<Trash2 size={15} aria-hidden />}
                        disabled={readOnly || submitting}
                        onClick={(event) => {
                          event.stopPropagation();
                          setTeacherToUnmark(leader);
                        }}
                      />
                      <ChevronRight size={16} aria-hidden />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      ) : (
        <>
          <PageHeader
            eyebrow="Teacher Operations · Teacher Leader"
            title={selectedLeader.teacherName}
            subtitle={
              [selectedLeader.emailId, selectedLeader.mobileNumber, selectedLeader.userId ? `User ID ${selectedLeader.userId}` : ""]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            actions={
              <Button variant="secondary" icon={<ArrowLeft size={16} aria-hidden />} onClick={closeLeader}>
                All leaders
              </Button>
            }
          />

          <Card
            title="Assign teachers"
            subtitle="Search by name or mobile number, pick teachers, then save."
            actions={<Badge tone="primary">{assigned.length} teachers</Badge>}
            className="tl-card"
          >
            <div className="tl-search" ref={assignWrapRef}>
              <SearchInput
                aria-label="Search teachers to add to this leader"
                placeholder="Search and add teachers to this leader"
                value={assignQuery}
                onChange={(e) => handleAssignQueryChange(e.target.value)}
                onFocus={() => {
                  setAssignMenuOpen(true);
                  if (assignQuery.trim().length >= 2) void searchTeachersToAssign(assignQuery);
                }}
                disabled={readOnly || submitting}
              />
              {assignMenuOpen
                ? renderSearchResults(assignSuggestions, assignSearching, assignQuery, addPendingTeacher, "Add")
                : null}
            </div>

            {pendingAssign.length > 0 ? (
              <div className="tl-pending">
                {pendingAssign.map((teacher) => (
                  <span key={teacher.teacherId} className="tl-chip">
                    {teacher.teacherName}
                    <button
                      type="button"
                      onClick={() =>
                        setPendingAssign((prev) => prev.filter((item) => item.teacherId !== teacher.teacherId))
                      }
                      disabled={submitting}
                      aria-label={`Remove ${teacher.teacherName}`}
                    >
                      <X size={12} aria-hidden />
                    </button>
                  </span>
                ))}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void confirmAssign()}
                  disabled={readOnly}
                  loading={submitting}
                >
                  {submitting ? "Saving..." : `Assign ${pendingAssign.length}`}
                </Button>
              </div>
            ) : null}
          </Card>

          <Card
            title="Aligned teachers"
            actions={<Badge>{visibleAssigned.length}</Badge>}
            className="tl-card"
          >
            <Toolbar>
              <SearchInput
                className="ds-toolbar__grow"
                aria-label="Filter aligned teachers"
                placeholder="Filter aligned teachers"
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
              />
            </Toolbar>
            {loadingAssigned ? (
              <Loader label="Loading teachers…" />
            ) : visibleAssigned.length === 0 ? (
              <EmptyState
                icon={<Users size={22} aria-hidden />}
                title={assignedFilter.trim() ? "No aligned teachers match that filter" : "No teachers aligned yet"}
                description={
                  assignedFilter.trim()
                    ? "Try part of the name, the email or the mobile number, or clear the filter."
                    : "Search above to add teachers to this leader."
                }
              />
            ) : (
              <div className="tl-people">
                {visibleAssigned.map((teacher) => (
                  <div key={teacher.teacherId} className="tl-person tl-person--static">
                    <Avatar name={teacher.teacherName} seed={teacher.teacherId} />
                    <div className="tl-person__main">
                      <p className="tl-person__name">{teacher.teacherName}</p>
                      <p className="tl-person__email">{teacher.emailId || "No email"}</p>
                    </div>
                    <div className="tl-person__side">
                      <div>{teacher.mobileNumber || "—"}</div>
                      <div className="tl-person__id">Teacher ID {teacher.teacherId}</div>
                    </div>
                    <div className="tl-person__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="tl-danger-ghost"
                        title="Remove"
                        aria-label={`Remove ${teacher.teacherName} from this leader`}
                        icon={<Trash2 size={15} aria-hidden />}
                        disabled={readOnly || submitting}
                        onClick={() => setTeacherToRemove(teacher)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      <Dialog
        open={addLeaderOpen}
        onClose={closeAddLeader}
        title="Add Teacher Leader"
        persistent
        actions={
          <>
            <Button variant="secondary" onClick={closeAddLeader} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void confirmCreateLeader()} loading={submitting}>
              {submitting ? "Saving..." : "Add leader"}
            </Button>
          </>
        }
      >
        <div className="tl-form">
          <Field label="First name" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                autoComplete="off"
                value={leaderForm.firstName}
                onChange={(e) => setLeaderForm((prev) => ({ ...prev, firstName: e.target.value }))}
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Last name" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                autoComplete="off"
                value={leaderForm.lastName}
                onChange={(e) => setLeaderForm((prev) => ({ ...prev, lastName: e.target.value }))}
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Email" required hint="Must be an @artiumacademy.com address." className="tl-form__full">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="email"
                autoComplete="off"
                placeholder="name@artiumacademy.com"
                value={leaderForm.email}
                onChange={(e) => setLeaderForm((prev) => ({ ...prev, email: e.target.value }))}
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Country code">
            {(ctl) => (
              <Select
                {...ctl}
                options={COUNTRY_CODE_OPTIONS}
                value={leaderForm.countryCode}
                onChange={(e) => setLeaderForm((prev) => ({ ...prev, countryCode: e.target.value }))}
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Mobile number" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                autoComplete="off"
                inputMode="numeric"
                value={leaderForm.mobileNumber}
                maxLength={leaderForm.countryCode === "+91" ? 10 : 15}
                onChange={(e) =>
                  setLeaderForm((prev) => ({ ...prev, mobileNumber: e.target.value.replace(/\D/g, "") }))
                }
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Role" className="tl-form__full">
            {(ctl) => <TextInput {...ctl} value="TEACHER_LEADER" disabled readOnly />}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(teacherToUnmark)}
        onClose={closeUnmarkModal}
        title="Remove Teacher Leader role?"
        actions={
          <>
            <Button variant="secondary" onClick={closeUnmarkModal} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void confirmUnmark()} loading={submitting}>
              {submitting ? "Removing..." : "Remove role"}
            </Button>
          </>
        }
      >
        <p>
          Remove the Teacher Leader role from <span className="ds-strong">{teacherToUnmark?.teacherName}</span>?
        </p>
      </Dialog>

      <Dialog
        open={Boolean(teacherToRemove)}
        onClose={closeRemoveModal}
        title="Remove teacher from this leader?"
        actions={
          <>
            <Button variant="secondary" onClick={closeRemoveModal} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void confirmRemoveAssigned()} loading={submitting}>
              {submitting ? "Removing..." : "Remove"}
            </Button>
          </>
        }
      >
        <p>
          Remove <span className="ds-strong">{teacherToRemove?.teacherName}</span> from {selectedLeader?.teacherName}?
        </p>
      </Dialog>
    </Page>
  );
};

export default TeacherLeader;
